import { type Camera, type Vector3 } from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { CameraMode, TunableSpec } from './cameraMode';

// E1 — the beat-1 establishing shot: a CONSTRAINED neck-turn from a fixed
// exterior standpoint. The visitor stands outside and can look around — a
// little left/right, a little up/down — but can't fly around or dolly in; the
// only way "in" is the go-inside affordance. It's a FreeformMode (OrbitControls)
// with the azimuth/polar swept down to a neck's range and zoom/pan/dolly locked.
//
// The standpoint is wherever the camera sits at init() (the boot exterior pose);
// the neck range pivots around the constructor's target. The yaw/pitch ranges +
// the standpoint are framing — John's eye (tunable in the dev panel, flagged).
const TUNABLES: TunableSpec[] = [
  { key: 'yawRange', label: 'neck yaw (rad)', min: 0.05, max: 1.4, step: 0.05 },
  { key: 'pitchRange', label: 'neck pitch (rad)', min: 0.02, max: 0.8, step: 0.02 },
  { key: 'rotateSpeed', label: 'rotate speed', min: 0.1, max: 2, step: 0.1 },
];

export class ExternalViewMode implements CameraMode {
  private controls: OrbitControls | null = null;
  // The orbit angles the constraint window centres on — captured at init from
  // the actual boot framing, so the neck turn is symmetric about where you face.
  private centerAz = 0;
  private centerPol = Math.PI / 2;

  private _yawRange = 0.6;    // ± rad ≈ ±34° left/right — a neck turn, not a pan
  private _pitchRange = 0.22; // ± rad ≈ ±13° up/down
  private _rotateSpeed = 0.7; // a touch slow — an establishing look, not a whip-pan

  get yawRange(): number { return this._yawRange; }
  set yawRange(v: number) { this._yawRange = v; this.applyLimits(); }
  get pitchRange(): number { return this._pitchRange; }
  set pitchRange(v: number) { this._pitchRange = v; this.applyLimits(); }
  get rotateSpeed(): number { return this._rotateSpeed; }
  set rotateSpeed(v: number) { this._rotateSpeed = v; if (this.controls) this.controls.rotateSpeed = v; }

  constructor(
    private camera: Camera,
    private domElement: HTMLElement,
    private target: Vector3,
  ) {}

  init(): void {
    const c = new OrbitControls(this.camera, this.domElement);
    c.enableDamping = true;
    c.dampingFactor = 0.08;
    c.rotateSpeed = this._rotateSpeed;
    c.enableZoom = false; // a fixed standpoint — no dolly
    c.enablePan = false;
    c.target.copy(this.target);
    c.update();
    // Lock the distance so even a touch-pinch can't push the standpoint.
    const d = this.camera.position.distanceTo(this.target);
    c.minDistance = d;
    c.maxDistance = d;
    // Centre the neck window on wherever the boot pose already faces.
    this.centerAz = c.getAzimuthalAngle();
    this.centerPol = c.getPolarAngle();
    this.controls = c;
    this.applyLimits();
  }

  /** Re-clamp the azimuth/polar window around the captured centre. */
  private applyLimits(): void {
    const c = this.controls;
    if (!c) return;
    c.minAzimuthAngle = this.centerAz - this._yawRange;
    c.maxAzimuthAngle = this.centerAz + this._yawRange;
    c.minPolarAngle = Math.max(0.01, this.centerPol - this._pitchRange);
    c.maxPolarAngle = Math.min(Math.PI - 0.01, this.centerPol + this._pitchRange);
    c.update();
  }

  update(): void {
    this.controls?.update();
  }

  dispose(): void {
    this.controls?.dispose();
    this.controls = null;
  }

  getTunables() {
    return { target: this, specs: TUNABLES };
  }
}
